import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

import * as authApi from '@/services/endpoints/auth'
import { clearTokens, getAccessToken, getRefreshToken, setTokens } from '@/services/tokenStorage'
import type {
  AuthTokens,
  LoginPayload,
  LoginResult,
  RegisterPayload,
  ResetPasswordPayload,
  SocialProvider,
} from '@/types/user'

/**
 * Owns the tokens only. Profile data lives in the user store, and the two are
 * orchestrated by `composables/useSession.ts`.
 */
export const useAuthStore = defineStore('auth', () => {
  // Seeded from localStorage so a reload keeps the session.
  const accessToken = ref<string | null>(getAccessToken())
  const refreshToken = ref<string | null>(getRefreshToken())

  const isAuthenticated = computed(() => accessToken.value !== null)

  function applyTokens(tokens: AuthTokens): void {
    setTokens(tokens)
    accessToken.value = tokens.accessToken
    refreshToken.value = tokens.refreshToken
  }

  /** Drops the local session without calling the API. */
  function clearSession(): void {
    clearTokens()
    accessToken.value = null
    refreshToken.value = null
  }

  /** Re-reads storage: used after the HTTP layer refreshed the pair itself. */
  function syncFromStorage(): void {
    accessToken.value = getAccessToken()
    refreshToken.value = getRefreshToken()
  }

  async function login(payload: LoginPayload): Promise<LoginResult> {
    const result = await authApi.login(payload)
    applyTokens({ accessToken: result.accessToken, refreshToken: result.refreshToken })
    return result
  }

  async function socialLogin(provider: SocialProvider, token: string): Promise<LoginResult> {
    const result = await authApi.socialLogin(provider, token)
    applyTokens({ accessToken: result.accessToken, refreshToken: result.refreshToken })
    return result
  }

  /** Second half of the Discord SSO redirect — see `authApi.exchangeDiscordLoginTicket`. */
  async function exchangeDiscordLoginTicket(ticket: string): Promise<LoginResult> {
    const result = await authApi.exchangeDiscordLoginTicket(ticket)
    applyTokens({ accessToken: result.accessToken, refreshToken: result.refreshToken })
    return result
  }

  /** Registration does not log the user in: the email must be verified first. */
  function register(payload: RegisterPayload) {
    return authApi.register(payload)
  }

  async function logout(): Promise<void> {
    try {
      // Best effort: even if the call fails we must drop the local session.
      if (isAuthenticated.value) await authApi.logout()
    } finally {
      clearSession()
    }
  }

  function forgotPassword(email: string) {
    return authApi.forgotPassword(email)
  }

  function resetPassword(payload: ResetPasswordPayload) {
    return authApi.resetPassword(payload)
  }

  function verifyEmail(id: string, hash: string) {
    return authApi.verifyEmail(id, hash)
  }

  function resendVerificationEmail() {
    return authApi.resendVerificationEmail()
  }

  return {
    accessToken,
    refreshToken,
    isAuthenticated,
    applyTokens,
    clearSession,
    syncFromStorage,
    login,
    socialLogin,
    exchangeDiscordLoginTicket,
    register,
    logout,
    forgotPassword,
    resetPassword,
    verifyEmail,
    resendVerificationEmail,
  }
})
